import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { notifications } from '@mantine/notifications';
import { Button, ControlledInput, ControlledTextarea, ControlledFileInput } from '@shared/ui';
import { useCreateCustomSet, useUpdateCustomSet } from '../../hooks/useCustomSets';
import { customSetFormSchema } from '../../utils/customSetValidation';
import type { CustomSet, UpdateCustomSetDto } from '../../types';
import './CustomSetForm.css';

interface CustomSetFormProps {
  customSet?: CustomSet;
  onSuccess?: () => void;
}

type FileInputValue = File | { id: number; url: string };

interface CustomSetFormData {
  name: string;
  description?: string;
  image?: FileInputValue | null;
}

export default function CustomSetForm({ customSet, onSuccess }: CustomSetFormProps) {
  const isEditMode = !!customSet;
  const resolver = yupResolver(customSetFormSchema) as any;

  const { handleSubmit, formState: { errors }, reset, control } = useForm<CustomSetFormData>({
    resolver,
    defaultValues: {
      name: customSet?.name || '',
      description: customSet?.description || '',
      image: customSet?.imageUrl ? { id: 0, url: customSet.imageUrl } : null,
    },
  });
  const createCustomSet = useCreateCustomSet();
  const updateCustomSet = useUpdateCustomSet();

  useEffect(() => {
    if (customSet) {
      reset({
        name: customSet.name,
        description: customSet.description || '',
        image: customSet.imageUrl ? { id: 0, url: customSet.imageUrl } : null,
      });
    }
  }, [customSet, reset]);

  const onSubmit = async (data: CustomSetFormData) => {
    try {
      const imageFile = data.image instanceof File ? data.image : undefined;

      if (isEditMode && customSet) {
        const updateData: UpdateCustomSetDto = {
          name: data.name,
          description: data.description || undefined,
        };

        // Определяем, было ли изображение удалено
        // Если изначально было изображение, а сейчас null - значит удалено
        const hadImage = customSet.imageUrl && customSet.imageUrl.length > 0;
        const imageRemoved = hadImage && data.image === null;

        if (imageRemoved) {
          updateData.shouldDeleteImage = true;
        }

        await updateCustomSet.mutateAsync({
          id: customSet.id,
          data: updateData,
          file: imageFile,
        });
      } else {
        await createCustomSet.mutateAsync({
          data: {
            name: data.name,
            description: data.description || undefined,
            // productIds не передаем, товары можно будет добавить позже
          },
          file: imageFile,
        });
      }

      notifications.show({
        title: 'Успешно',
        message: `Кастомная подборка успешно ${isEditMode ? 'обновлена' : 'создана'}`,
        color: 'green',
      });

      onSuccess?.();
    } catch (error) {
      notifications.show({
        title: 'Ошибка',
        message: `Ошибка при ${isEditMode ? 'обновлении' : 'создании'} кастомной подборки`,
        color: 'red',
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="custom-set-form">
      <div className="form-group">
        <ControlledInput
          id="name"
          type="text"
          label="Название *"
          name="name"
          control={control}
          error={errors.name}
        />
      </div>

      <div className="form-group">
        <ControlledTextarea
          id="description"
          rows={4}
          label="Описание"
          name="description"
          control={control}
          error={errors.description}
        />
      </div>

      <div className="form-group">
        <ControlledFileInput
          id="image"
          name="image"
          label="Обложка"
          accept="image/*"
          control={control}
          error={errors.image}
        />
      </div>

      <Button
        type="submit"
        loading={createCustomSet.isPending || updateCustomSet.isPending}
        className="custom-set-form__submit-button"
      >
        {isEditMode ? 'Обновить подборку' : 'Создать подборку'}
      </Button>
    </form>
  );
}

